

/**
 * Normalizes a name string (trims, lowercases, and capitalizes each word).
 * @param {string} n - The name string.
 * @returns {string} The normalized name.
 */
export const normaliseName = n =>
  (n || '')
    .trim()
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

/**
 * Calculates the start date of the payroll week for a given date.
 * @param {Date | string} date - The date within the payroll week.
 * @param {number} startDayIndex - The index of the day the week starts (0=Sun, 1=Mon, etc.).
 * @returns {Date} The start date of the payroll week (Time 00:00:00:000).
 */
export const getPayrollWeekStart = (date, startDayIndex) => {
  const d = new Date(date)
  const dayOfWeek = d.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  let diff = dayOfWeek - startDayIndex
  if (diff < 0) {
    diff += 7
  }

  const start = new Date(d)
  start.setDate(d.getDate() - diff)
  
  start.setHours(0, 0, 0, 0) 
  return start
}

/**
 * Groups time entries by payroll week and aggregates hours, bonus, and commute.
 * @param {Array<Object>} entries - The array of time entry objects.
 * @param {number} startDayIndex - The index of the day the week starts (0=Sun, 1=Mon, etc.).
 * @returns {Array<Object>} An array of weekly payroll summary objects.
 */
export const groupByPayrollWeek = (entries, startDayIndex = 0) => {
  const grouped = {}
  entries
    .filter(entry => entry && entry.date)
    .forEach(entry => {
      const name = normaliseName(entry.name || '[No Name]')
      const date = new Date(entry.date)
      
      const start = getPayrollWeekStart(date, startDayIndex)
      
      const end = new Date(start)
      end.setDate(start.getDate() + 6)

      const startKey = start.toISOString().split('T')[0]
      const endKey = end.toISOString().split('T')[0]
      const key = `${name}-${startKey}-${endKey}`
      
      if (!grouped[key]) {
        grouped[key] = {
          name,
          start: start.toLocaleDateString(undefined, { timeZone: 'UTC' }),
          end: end.toLocaleDateString(undefined, { timeZone: 'UTC' }),
          totalHours: 0,
          bonus: 0,
          commute: 0
        }
      }
      grouped[key].totalHours += Number(entry.hoursWorked) || 0
      grouped[key].bonus += Number(entry.bonus) || 0
      grouped[key].commute += Number(entry.commute) || 0
    })
  return Object.values(grouped)
}